<template>
  <div class="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
    <div class="w-full max-w-sm space-y-10">
      <div>
        <img class="mx-auto h-30 w-auto dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <img class="mx-auto h-30 w-auto not-dark:hidden" :src="logoUrl" alt="ManageRunners logo" />
        <h2
          class="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 dark:text-white"
        >
          {{ otpAuthUrl ? 'Set up two-factor authentication' : 'Choose a new password' }}
        </h2>
      </div>

      <form
        v-if="otpAuthUrl"
        class="space-y-6"
        novalidate
        data-testid="mfa-reenroll"
        @submit.prevent="onVerifyMfa"
      >
        <div class="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200" role="status">
          Your password is updated and your email is verified. For your security, two-factor
          authentication was reset: your old authenticator entry no longer works.
        </div>
        <div class="space-y-2">
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Scan this QR code with your authenticator app, or use the link below:
          </p>
          <img v-if="qrCodeDataUrl" :src="qrCodeDataUrl" alt="2FA QR code" class="mx-auto h-60 w-60" />
          <div v-else class="mx-auto h-60 w-60 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          <p class="break-all text-xs text-gray-500 dark:text-gray-400">{{ otpAuthUrl }}</p>
        </div>
        <div>
          <label for="mfa-email" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Email</label
          >
          <input
            id="mfa-email"
            v-model.trim="email"
            type="email"
            autocomplete="username"
            required
            :disabled="verifying"
            class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:focus:outline-indigo-500"
          />
        </div>
        <div>
          <label for="mfa-otp" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
            >Enter the 6-digit code from the app</label
          >
          <input
            id="mfa-otp"
            v-model="otp"
            type="text"
            inputmode="numeric"
            pattern="\d{6}"
            maxlength="6"
            autocomplete="one-time-code"
            required
            :disabled="verifying"
            class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:focus:outline-indigo-500"
            placeholder="6-digit code"
          />
        </div>
        <div class="space-y-2">
          <button
            type="submit"
            :disabled="verifying || !otp || !email"
            class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            <span v-if="verifying">Verifying…</span>
            <span v-else>Verify & sign in</span>
          </button>
          <p v-if="verifyError" class="text-sm text-red-600 dark:text-red-400" role="alert">
            {{ verifyError }}
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400">
            You need this authenticator entry every time you sign in. We sign you in with your new
            password.
          </p>
        </div>
      </form>

      <form v-else-if="!success" class="space-y-6" novalidate @submit.prevent="onSubmit">
        <div v-if="!token" class="text-sm text-red-600 dark:text-red-400" role="alert">
          This reset link is invalid or incomplete. Please request a new one.
        </div>

        <div v-else>
          <div class="relative">
            <input
              id="password"
              name="password"
              type="password"
              autocomplete="new-password"
              required
              autofocus
              aria-label="New password"
              class="block w-full rounded-t-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              placeholder="New password"
              v-model="password"
              :disabled="loading"
              @blur="showPasswordError = false"
              @focus="showPasswordError = true"
            />
            <button
              v-if="submitAttempted && !isPasswordValid && !showPasswordError"
              type="button"
              class="absolute pointer-events-none right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-600 shadow-sm"
              aria-label="Password does not meet requirements"
            >
              !
            </button>
            <div
              v-if="submitAttempted && !isPasswordValid && showPasswordError"
              class="absolute left-full top-1/2 -translate-y-1/2 ml-2 right-0 mt-1 w-72 rounded-md border border-red-300 bg-white p-2 text-xs text-red-700 shadow-lg dark:bg-gray-900 dark:text-red-400 dark:border-red-700"
              role="alert"
            >
              Password must be at least 8 characters and include uppercase, lowercase, number, and
              symbol.
            </div>
          </div>
          <div class="-mt-px relative">
            <input
              id="verifyPassword"
              name="verifyPassword"
              type="password"
              autocomplete="new-password"
              required
              aria-label="Verify new password"
              class="block w-full rounded-b-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
              placeholder="Verify new password"
              v-model="verifyPassword"
              :disabled="loading"
              @blur="showVerifyPasswordError = false"
              @focus="showVerifyPasswordError = true"
            />
            <button
              v-if="
                submitAttempted &&
                password &&
                verifyPassword &&
                !passwordsMatch &&
                !showVerifyPasswordError
              "
              type="button"
              class="absolute pointer-events-none right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-red-300 bg-red-100 text-red-600 shadow-sm"
              aria-label="Passwords do not match"
            >
              !
            </button>
            <div
              v-if="
                submitAttempted &&
                password &&
                verifyPassword &&
                !passwordsMatch &&
                showVerifyPasswordError
              "
              class="absolute left-full top-1/2 -translate-y-1/2 ml-2 right-0 mt-1 w-64 rounded-md border border-red-300 bg-white p-2 text-xs text-red-700 shadow-lg dark:bg-gray-900 dark:text-red-400 dark:border-red-700"
              role="alert"
            >
              Passwords do not match.
            </div>
          </div>
        </div>

        <div class="space-y-2">
          <button
            type="submit"
            :disabled="loading || !token"
            class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:shadow-none dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500"
          >
            <span v-if="loading">Updating…</span>
            <span v-else>Update password</span>
          </button>
          <p v-if="error" class="text-sm text-red-600 dark:text-red-400" role="alert">
            {{ error }}
          </p>
        </div>
      </form>

      <div
        v-else
        class="rounded-md border border-gray-200 p-4 text-sm text-green-700 dark:border-white/10 dark:text-green-400"
        role="status"
      >
        Your password has been updated. You can sign in with your new password.
      </div>

      <p v-if="!otpAuthUrl" class="text-center text-sm/6 text-gray-500 dark:text-gray-400">
        <router-link
          :to="{ name: 'Login' }"
          class="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >Back to sign in</router-link
        >
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import QRCode from 'qrcode';
import {useRoute, useRouter} from 'vue-router';
import {useAuthStore} from '@/stores/auth';
import {useToastStore} from '@/stores/toast';

const logoUrl = new URL('../assets/logo_runner_small.png', import.meta.url).href;
const auth = useAuthStore();
const toast = useToastStore();
const route = useRoute();
const router = useRouter();

const token = computed(() => {
  const value = route.query.token;
  return typeof value === 'string' ? value : Array.isArray(value) ? (value[0] ?? '') : '';
});

const password = ref('');
const verifyPassword = ref('');
const loading = ref(false);
const error = ref<string | null>(null);
const success = ref(false);
const submitAttempted = ref(false);
const showPasswordError = ref(false);
const showVerifyPasswordError = ref(false);
const otpAuthUrl = ref<string | null>(null);
const qrCodeDataUrl = ref<string | null>(null);
const email = ref('');
const otp = ref('');
const verifying = ref(false);
const verifyError = ref<string | null>(null);

watch(otpAuthUrl, async (url) => {
  qrCodeDataUrl.value = null;
  if (!url) return;
  try {
    qrCodeDataUrl.value = await QRCode.toDataURL(url, { margin: 1, width: 240 });
  } catch {
    // The otpauth link below stays usable without the image.
  }
});

const passwordsMatch = computed(() => password.value === verifyPassword.value);
const isPasswordValid = computed(() => checkPassword(password.value));

function checkPassword(value: string): boolean {
  if (!value) return false;
  const hasMinLength = value.length >= 8;
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasDigit = /\d/.test(value);
  const hasSymbol = /[^A-Za-z0-9]/.test(value);
  return hasMinLength && hasUppercase && hasLowercase && hasDigit && hasSymbol;
}

async function onSubmit() {
  submitAttempted.value = true;
  error.value = null;
  if (!token.value) return;
  if (!isPasswordValid.value || !passwordsMatch.value) return;

  loading.value = true;
  try {
    const result = await auth.resetPassword({ token: token.value, newPassword: password.value });
    if (result?.mfaReenrolled && result.otpAuthUrl) {
      otpAuthUrl.value = result.otpAuthUrl;
      return;
    }
    success.value = true;
    toast.success('Password updated. Please sign in.');
    await router.push({ name: 'Login' });
  } catch (e) {
    const status = (e as { response?: { data?: { status?: string } } } | null)?.response?.data
      ?.status;
    if (status === 'INVALID_PASSWORD') {
      error.value =
        'Password must be at least 8 characters and include uppercase, lowercase, number, and symbol.';
    } else if (status === 'INVALID_TOKEN') {
      error.value = 'This reset link is invalid or has expired. Please request a new one.';
    } else {
      error.value = auth.error || 'Unable to reset password. Please try again.';
    }
  } finally {
    loading.value = false;
  }
}

async function onVerifyMfa() {
  if (!otp.value || !email.value) return;
  verifying.value = true;
  verifyError.value = null;
  try {
    await auth.login({ username: email.value, password: password.value });
    if (auth.mfaRequired) {
      await auth.verifyMfa(otp.value);
    }
    if (!auth.isAuthenticated) {
      verifyError.value = 'Verification failed. Please try again.';
      return;
    }
    otp.value = '';
    password.value = '';
    verifyPassword.value = '';
    toast.success('Password updated and two-factor authentication set up.');
    await router.push({ name: 'Dashboard' });
  } catch (e: unknown) {
    verifyError.value = auth.error || (e instanceof Error && e.message) || 'Failed to verify';
  } finally {
    verifying.value = false;
  }
}
</script>
